import { Spin } from 'antd';
import { Link, useParams } from 'react-router';
import { EmptyState } from '@/components/EmptyState';
import { StatusDot } from '@/components/StatusDot';
import { TEMPLATE_STATES } from '@/lib/labels';
import { templateStateOf, templateStateWordOf } from '@/lib/statusPresentation';
import { LIBRARY_COPY, PANE_COPY } from '../copy';
import { useTemplateList, useTemplateSet } from '../templateQueries';
import { ForceMatrix } from './ForceMatrix';
import { openCodesIn, sumForce } from './paneData';
import { usePaneHeadingRef } from './paneFocus';
import { partHeaderKeyOf, partHeadingOf, sectionCodeOf } from './templateTree';

/**
 * ─────────────────────────────────────────────────────────────────────────────────────
 *  [P7B.1 §2] ONE PART — the review site's `partDetail`: its title, pages and counts, its sections, and
 *  the force every duty point in it asks for.
 *
 *  ⚠️ IT READS THE SET AND THE LIST, the two reads the library has already made — one cache, so opening
 *  a Part costs no request. The state counts are `templateStateOf`'s, the tree's own.
 * ─────────────────────────────────────────────────────────────────────────────────────
 */

const PartDetail = (): React.ReactElement => {
  const { setKey = '', partId = '' } = useParams();
  const set = useTemplateSet(setKey, { pane: true });
  const list = useTemplateList(setKey, { pane: true });
  const headingRef = usePaneHeadingRef();

  if (set.isPending || list.isPending) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }
  const sections = (set.data?.sections ?? []).filter((section) => section.part === partId);
  if (sections.length === 0) return <EmptyState title={PANE_COPY.noPart} />;
  const items = (list.data?.items ?? []).filter((item) => item.part === partId);
  const pages = sections.flatMap((section) => section.pages);
  const states = TEMPLATE_STATES.map((state) => ({
    state,
    count: items.filter((item) => templateStateOf(item) === state).length,
  })).filter((entry) => entry.count > 0);

  return (
    <article className="flex flex-col gap-5" data-testid="part-view">
      <header className="flex flex-col gap-1">
        <h2 key={partId} ref={headingRef} tabIndex={-1} className="text-lg font-semibold text-heading outline-none">
          {partHeadingOf(partId, set.data?.sections ?? [])}
        </h2>
        <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground" data-testid="part-meta">
          {pages.length > 0 ? <span>{PANE_COPY.pages(pages)}</span> : null}
          <span>{PANE_COPY.templates(items.length)}</span>
          <span>{PANE_COPY.dutyPoints(items.reduce((sum, item) => sum + item.dutyPointCount, 0))}</span>
        </p>
        {states.length > 0 ? (
          <p className="flex flex-wrap gap-x-4 gap-y-1" data-testid="part-states">
            {states.map(({ state, count }) => {
              const word = templateStateWordOf(state);
              return <StatusDot key={state} tone={word.tone} label={`${word.label}: ${String(count)}`} />;
            })}
          </p>
        ) : null}
      </header>

      <section className="flex flex-col gap-2" data-testid="part-sections">
        <h3 className="font-medium text-heading">{PANE_COPY.sectionsTitle}</h3>
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted text-xs text-muted-foreground">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  {PANE_COPY.section}
                </th>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  {PANE_COPY.covers}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {PANE_COPY.pagesColumn}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {PANE_COPY.templatesColumn}
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  {PANE_COPY.dutyPointsColumn}
                </th>
              </tr>
            </thead>
            <tbody>
              {sections.map((section) => {
                const own = items.filter((item) => item.sectionKey === section.key);
                const header = section.key === partHeaderKeyOf(partId);
                return (
                  <tr key={section.key} className="border-t border-border">
                    <td className="px-3 py-1.5">
                      {header ? (
                        LIBRARY_COPY.part(partId)
                      ) : (
                        <Link
                          to={`/templates/${setKey}/section/${encodeURIComponent(section.key)}`}
                          className="text-link"
                        >
                          {sectionCodeOf(section.key)}
                        </Link>
                      )}
                    </td>
                    <td className="px-3 py-1.5">{section.title}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {PANE_COPY.pages(section.pages).replace(/^Pages? /, '')}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{own.length}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {own.reduce((sum, item) => sum + item.dutyPointCount, 0)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {items.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h3 className="font-medium text-heading">{PANE_COPY.forcePart}</h3>
          <ForceMatrix
            totals={sumForce(items)}
            openColumns={openCodesIn(items, 'FORCE_COLUMN')}
            openSources={openCodesIn(items, 'SOURCE')}
            caption={PANE_COPY.forceCaption}
            testId="part-force"
          />
        </section>
      ) : null}
    </article>
  );
};

export default PartDetail;
